import { useState } from 'react'
import { getFileUrl } from '@/lib/s3'
import { apiFetch } from '@/lib/api'

const generateServerThumbnail = async (key, mediaType) => {
    try {
        const response = await apiFetch('/api/upload/thumbnail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key, mediaType }),
        })

        if (!response.ok) {
            console.warn('Thumbnail request failed', response.status)
            return { thumbnail_url: null, thumbnail_key: null }
        }

        return await response.json()
    } catch (err) {
        console.warn('Thumbnail request error', err)
        return { thumbnail_url: null, thumbnail_key: null }
    }
}

export default function useFileUpload() {
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploadError, setUploadError] = useState(null)
    const [isUploading, setIsUploading] = useState(false)

    const uploadFile = async (file, folder = 'assets') => {
        if(!file) return
        
        try {
            setUploadProgress(0)
            setUploadError(null)
            setIsUploading(true)
            const fileType = file.type.split('/')[0]
            const fileTypes = ['image', 'video', 'audio']
            const mediaType = fileTypes.includes(fileType) ? fileType : 'none'

            // Get signed URL for upload
            const response = await apiFetch('/api/upload', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fileName: file.name,
                    fileType: file.type,
                    folder: folder
                }),
            })

            if (!response.ok) throw new Error('Failed to get upload URL')

            const { signedUrl, key } = await response.json()

            // Upload file with progress tracking
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest()
                
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = Math.round((event.loaded / event.total) * 100)
                        setUploadProgress(percentComplete)
                    }
                })

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve()
                    } else {
                        reject(new Error('Upload failed'))
                    }
                })

                xhr.addEventListener('error', () => {
                    reject(new Error('Upload failed'))
                })

                xhr.open('PUT', signedUrl)
                xhr.setRequestHeader('Content-Type', file.type)
                xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')
                xhr.setRequestHeader('Cache-Control', 'public, max-age=31536000, immutable')
                xhr.setRequestHeader('x-ms-blob-cache-control', 'public, max-age=31536000, immutable')
                xhr.send(file)
            })

            const fileUrl = getFileUrl(key)

            const { thumbnail_url, thumbnail_key } = await generateServerThumbnail(key, mediaType)

            return {
                url: fileUrl,
                key: key,
                mediaType,
                thumbnail_url: thumbnail_url ?? (mediaType === 'image' ? fileUrl : null),
                thumbnail_key: thumbnail_key ?? (mediaType === 'image' ? key : null),
            }

        } catch (err) {
            setUploadError(err.message)
            throw err
        } finally {
            setUploadProgress(0)
            setIsUploading(false)
        }
    }

    return {
        uploadFile,
        uploadProgress,
        uploadError,
        isUploading
    }
}
